/**
 * One group run on Live Sessions (GROUP-ORIENTATION-PLAN Phase 5): the
 * "second screen for whoever is watching" — status, leader, paused-by,
 * joined tally, and per-member connected/check state, as ONE row instead
 * of N assignment rows. Presentation only.
 */
import type { GroupRunRowData } from '@/hooks/useGroupRuns';
import { joinedTally } from '@/lib/groupRunEntity';
import { GatedButton } from '@/components/GatedButton';

const STATUS_COLOR: Record<string, string> = {
  Running: 'var(--good)',
  Paused: 'var(--warn)',
};

export function GroupRunLiveRow({
  run,
  stepLabel,
  joinBusy,
  joinDisabledReason,
  onJoin,
  cancelBusy,
  onCancel,
}: {
  run: GroupRunRowData;
  /** The run's current step, resolved by the page (name or id). */
  stepLabel: string | null;
  joinBusy: boolean;
  joinDisabledReason: string | null;
  onJoin: () => void;
  cancelBusy: boolean;
  /** Cancel the run (the archive dialog sends coordinators here for this). */
  onCancel: () => void;
}) {
  const tally = joinedTally(run.members);
  return (
    <div style={{ borderTop: '1px solid var(--border-soft, var(--border))', padding: '10px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <b style={{ fontSize: 13 }}>{run.name}</b>
        <span style={{ color: STATUS_COLOR[run.statusTag] ?? 'var(--muted)', fontSize: 11, fontWeight: 600 }}>
          {run.statusTag}
          {run.statusTag === 'Paused' && run.pausedByEmail
            ? ` by ${run.pausedByEmail.split('@')[0]}`
            : ''}
        </span>
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>
          {tally.joined} of {tally.total} joined · leader {run.leaderEmail.split('@')[0]}
          {stepLabel ? ` · ${stepLabel}` : ''}
        </span>
        <GatedButton
          className="btn sm ghost"
          style={{ marginLeft: 'auto' }}
          busy={joinBusy}
          reason={joinDisabledReason}
          hint="Watch this group from inside the MineZero client — free camera, read-only chat."
          onClick={onJoin}
        >
          {joinBusy ? 'Joining…' : 'Join'}
        </GatedButton>
        <button
          type="button"
          className="btn sm danger"
          disabled={cancelBusy}
          title="Close the shared session. Every member carries on self-paced in their own session, keeping their progress."
          onClick={onCancel}
        >
          {cancelBusy ? 'Cancelling…' : 'Cancel'}
        </button>
      </div>
      <div style={{ marginTop: 4, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {run.members.map((m) => (
          <span key={m.email} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5 }}>
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: m.connected ? 'var(--good)' : 'var(--panel-3)',
                border: '1px solid var(--border)',
              }}
            />
            <span style={{ color: m.connected ? 'var(--text)' : 'var(--muted)' }}>
              {m.displayName || m.email}
            </span>
            {m.pendingChecks > 0 ? (
              <span style={{ color: 'var(--warn)' }}>({m.pendingChecks} open)</span>
            ) : null}
          </span>
        ))}
      </div>
    </div>
  );
}
